import StringUtils from "../utils/StringUtils.js";
import StorageService from "../services/StorageService.js";
import SoundManager from "../utils/SoundManager.js";
import FeedbackEffects from "../utils/FeedbackEffects.js";
import ScoreUtils from "../utils/ScoreUtils.js";
import AccountSync from "../services/AccountSync.js";
import { sanitizeRun } from "../services/syncMerge.js";
import { saveWeekRun } from "../utils/WeekRun.js";
import { formatDuration } from "../utils/TimeUtils.js";
import { getFlagPath } from "../utils/FlagUtils.js";
import { enterEndGame } from "../utils/EndGameUI.js";
import { showConfirmation } from "../utils/ConfirmationPopup.js";

/**
 * US States, mode List : une seule liste à plat des 50 États (pas de grille
 * par lettre comme Country Nerd (trop creuse avec seulement 50 entrées). On
 * tape un nom, la bonne case se remplit à sa place alphabétique.
 *
 * Ne dérive pas de BaseGame (pas de système de vies : ici on ne "perd" jamais,
 * on complète la liste à son rythme) mais persiste sa progression sous les
 * MÊMES clés que les jeux "pool" de BaseGame (`countriesleft`, `best`,
 * `bestRun`, `startedAt`, `errors`, `finished`) : ça suffit pour que le
 * classement (`extractEntry`/`getGameBest` dans leaderboard.js, la branche
 * générique "bestRun ou fallback .best") et `/parties-en-cours`
 * (`gameCatalog.js`, `kind: "pool"`) le traitent sans aucun code spécifique.
 */
export default class UsStatesList {
	constructor() {
		this.gameId = "usstateslist";
		this.page = document.querySelector(".page.us-states-list");
		if (!this.page) return;

		this.answers = this.page.querySelector(".answers");
		this.answerContainer = this.page.querySelector(".answer-container");
		this.answerInput = this.page.querySelector("#us-states-answer-input");
		this.answerButton = this.page.querySelector("#us-states-answer-validate");
		this.tipCurrent = this.page.querySelector(".tip .found");
		this.tipTotal = this.page.querySelector(".tip .total");
		this.hintDisplay = this.page.querySelector(".hint-display");

		this.states = geoNerdApp.usStates;
		this.total = this.states.length;
		this.sortedStates = [...this.states].sort((a, b) => a.sanitize.localeCompare(b.sanitize));

		this.buildInputs();
		this.buildHint();
		this.restoreProgress();
	}

	startClock() {
		const saved = StorageService.load(`${this.gameId}.startedAt`);
		if (saved) {
			this.startedAt = saved;
		} else {
			this.startedAt = Date.now();
			StorageService.save(`${this.gameId}.startedAt`, this.startedAt);
		}
	}

	/** Secondes écoulées, tronquées comme les autres chronos du site (jamais arrondies). */
	elapsedSeconds() {
		return Math.max(0, Math.floor((Date.now() - this.startedAt) / 1000));
	}

	startTicker() {
		this.stopTicker();
		const tip = this.page.querySelector(".tip");
		if (!tip) return;
		if (!tip.querySelector(".elapsed")) {
			tip.insertAdjacentHTML("beforeend", `<span class="elapsed"><span class="elapsed-icon" aria-hidden="true"></span><span class="elapsed-value"></span></span>`);
		}
		this.elapsedEl = tip.querySelector(".elapsed-value");
		const tick = () => { this.elapsedEl.textContent = formatDuration(this.elapsedSeconds()); };
		tick();
		this.ticker = setInterval(tick, 1000);
	}

	stopTicker() {
		if (!this.ticker) return;
		clearInterval(this.ticker);
		this.ticker = null;
	}

	restoreProgress() {
		const saved = StorageService.load(`${this.gameId}.countriesleft`);
		// Pool persisté vide = partie déjà gagnée avant ce chargement, comme
		// BaseGame.initCountries(), voir completeGame().
		this.alreadyFinishedOnLoad = Array.isArray(saved) && saved.length === 0;
		this.statesLeft = Array.isArray(saved) ? saved : [...this.states];

		const leftCodes = new Set(this.statesLeft.map(s => s.code));
		this.states.forEach(s => { s.found = !leftCodes.has(s.code); });

		this.errorsCount = parseInt(StorageService.load(`${this.gameId}.errors`, 0), 10) || 0;

		this.answers.innerHTML = "";
		let placeholderIndex = 0;
		this.sortedStates.forEach(state => {
			this.answers.appendChild(state.found ? this.buildAnswerEl(state) : this.buildPlaceholderEl(placeholderIndex++));
		});

		const foundCount = this.total - this.statesLeft.length;
		if (this.tipCurrent) this.tipCurrent.textContent = foundCount;
		if (this.tipTotal) this.tipTotal.textContent = this.total;

		if (foundCount < this.total) {
			this.startClock();
			// Lire « Comment jouer ? » avant le premier État ne compte pas dans le temps.
			document.addEventListener("geonerd:gameinfo-closed", () => {
				if (this.total === this.statesLeft.length && this.errorsCount === 0) {
					this.startedAt = Date.now();
					StorageService.save(`${this.gameId}.startedAt`, this.startedAt);
				}
			});
			this.startTicker();
			this.renderHint();
		} else {
			StorageService.save(`${this.gameId}.finished`, true);
			this.showWinState();
		}
	}

	buildAnswerEl(state) {
		const el = document.createElement("div");
		el.className = "answer valid";
		el.dataset.sanitize = state.sanitize;
		el.innerHTML = `${state.name}<span class="flag" style="background-image: url(${getFlagPath(`us-${state.code}`)})"></span>`;
		return el;
	}

	// Case vide d'un État encore à trouver : indique combien il en reste sans rien révéler.
	buildPlaceholderEl(index = 0) {
		const widths = ["6rem", "9rem", "7rem", "11rem", "8rem"];
		const el = document.createElement("div");
		el.className = "answer placeholder";
		el.setAttribute("aria-hidden", "true");
		el.innerHTML = `<span class="placeholder-name" style="width:${widths[index % widths.length]}"></span><span class="placeholder-flag"></span>`;
		return el;
	}

	// Remplace le placeholder du rang alphabétique de l'État par sa réponse trouvée.
	placeFoundState(state) {
		const el = this.buildAnswerEl(state);
		const rank = this.sortedStates.findIndex(s => s.code === state.code);
		const slots = this.answers.querySelectorAll(".answer.valid, .answer.placeholder");
		const target = slots[rank];
		if (target) {
			this.answers.replaceChild(el, target);
		} else {
			this.answers.appendChild(el);
		}
		return el;
	}

	buildInputs() {
		this.answerInput.addEventListener("input", () => this.autoValidate());
		this.answerInput.addEventListener("keydown", e => {
			if (e.key === "Enter") {
				e.preventDefault();
				this.validateAnswer(this.answerInput.value);
				this.keepKeyboardOpen();
			}
		});
		const submit = () => {
			this.validateAnswer(this.answerInput.value);
			this.keepKeyboardOpen();
		};
		this.answerButton.addEventListener("touchstart", e => { e.preventDefault(); submit(); }, { passive: false });
		this.answerButton.addEventListener("click", submit);
		this.bindRestart();
	}

	/**
	 * Valide sans attendre Entrée dès que la saisie correspond exactement (aux
	 * accents, espaces et tirets près) à un État encore à trouver. Seulement sur
	 * une égalité stricte : la tolérance aux fautes de frappe de
	 * `validateAnswer()` validerait un nom à moitié tapé. Et pas si ce nom est le
	 * début d'un autre État manquant (« Virginie » avant « Virginie-Occidentale ») :
	 * le joueur est peut-être en train de taper le second, Entrée tranche alors.
	 */
	autoValidate() {
		const typed = StringUtils.sanitize(this.answerInput.value.trim());
		if (!typed) return;
		const pool = this.states.filter(s => !s.found);
		const match = pool.find(s => s.sanitize === typed);
		if (!match) return;
		if (pool.some(s => s !== match && s.sanitize.startsWith(typed))) return;
		this.validateAnswer(this.answerInput.value);
	}

	keepKeyboardOpen() {
		if (this.statesLeft.length > 0) this.answerInput.focus({ preventScroll: true });
	}

	bindRestart() {
		this.page.querySelectorAll(".reload").forEach(btn => {
			btn.addEventListener("click", () => {
				["countriesleft", "errors", "startedAt", "finished", "hints"].forEach(key => StorageService.remove(`${this.gameId}.${key}`));
				window.location.reload();
			});
		});
	}

	buildHint() {
		this.hintButton = this.page.querySelector(".btn-hint");
		if (!this.hintButton) return;
		this.hintButton.addEventListener("click", () => {
			const useHint = () => {
				this.revealHint();
				this.answerInput.focus({ preventScroll: true });
			};

			// Avertissement une seule fois (pas à chaque indice) : passé le premier
			// clic confirmé, le joueur sait déjà ce qu'il paie.
			const warnedKey = `geonerd.${this.gameId}.hintWarned`;
			if (localStorage.getItem(warnedKey) === "true") {
				useHint();
				return;
			}

			const lang = geoNerdApp.languageManager;
			showConfirmation(
				lang.t("settings.confirmTitle"),
				lang.t("game.hintConfirmMessage"),
				() => {
					localStorage.setItem(warnedKey, "true");
					useHint();
				}
			);
		});
	}

	/**
	 * Révèle une lettre de plus du premier État encore à trouver (ordre
	 * alphabétique, comme la liste), au prix d'une faute — même règle que
	 * Country Nerd. Contrairement à Country Nerd, rien ne "connaît" déjà une
	 * première lettre gratuite (pas de grille de lettres ici), donc le premier
	 * indice révèle bien la première lettre plutôt que la deuxième.
	 */
	revealHint() {
		if (this.statesLeft.length === 0) return;
		const next = this.sortedStates.find(s => !s.found);
		if (!next) return;

		const hints = this.loadHints();
		const already = hints[next.sanitize] || 0;
		const revealed = Math.min(already + 1, next.name.length);
		if (revealed === already) return;

		hints[next.sanitize] = revealed;
		this.saveHints(hints);

		this.errorsCount++;
		StorageService.save(`${this.gameId}.errors`, this.errorsCount);

		this.renderHint();
	}

	/** Affiche le préfixe déjà payé du prochain État à trouver. */
	renderHint() {
		if (!this.hintDisplay) return;
		const next = this.sortedStates.find(s => !s.found);
		const revealed = next ? (this.loadHints()[next.sanitize] || 0) : 0;

		if (!next || revealed === 0) {
			this.hintDisplay.textContent = "";
			return;
		}

		const prefix = next.name.slice(0, revealed);
		const rest = "·".repeat(Math.max(0, next.name.replace(/\s/g, "").length - revealed));
		this.hintDisplay.innerHTML = `<strong>${prefix}</strong>${rest}`;
	}

	loadHints() {
		const saved = StorageService.load(`${this.gameId}.hints`);
		return saved && typeof saved === "object" ? saved : {};
	}

	saveHints(hints) {
		StorageService.save(`${this.gameId}.hints`, hints);
	}

	validateAnswer(answer) {
		// Valider à vide ne coûte rien, même règle que Country Nerd.
		if (!answer || !answer.trim()) return;
		const pool = this.states.filter(s => !s.found);
		const { item, score, ambiguous } = StringUtils.bestMatch(answer, pool, c => [c.sanitize]);
		const win = !!item && score >= 0.78 && !ambiguous;

		// Un État déjà trouvé retapé n'est plus dans le pool ci-dessus, donc
		// `bestMatch` ne peut jamais le retrouver et le prendrait pour une
		// faute. On le détecte à part et on l'ignore sans sanction.
		if (!win) {
			const foundPool = this.states.filter(s => s.found);
			const alreadyFound = StringUtils.bestMatch(answer, foundPool, c => [c.sanitize]);
			if (alreadyFound.item && alreadyFound.score >= 0.78 && !alreadyFound.ambiguous) {
				this.answerInput.value = "";
				return;
			}
		}

		if (win) {
			this.answerInput.value = "";
			item.found = true;
			const answerEl = this.placeFoundState(item);
			SoundManager.correct();
			FeedbackEffects.confetti(answerEl);
			this.statesLeft = this.statesLeft.filter(s => s.code !== item.code);
			const foundCount = this.total - this.statesLeft.length;
			if (this.tipCurrent) this.tipCurrent.textContent = foundCount;

			StorageService.save(`${this.gameId}.countriesleft`, this.statesLeft);
			this.saveBestSnapshot(foundCount);
			// L'indice suit la progression : il repart sur le prochain État manquant.
			this.renderHint();

			gsap.to(this.answerInput, {
				backgroundColor: "#25961c",
				duration: 0.2,
				onComplete: () => gsap.to(this.answerInput, { backgroundColor: "white", duration: 0.2 })
			});

			if (this.statesLeft.length === 0) this.completeGame();
		} else {
			this.errorsCount++;
			StorageService.save(`${this.gameId}.errors`, this.errorsCount);
			SoundManager.wrong();
			FeedbackEffects.shake(this.page.querySelector(".input-container"));
			gsap.to(this.answerInput, {
				backgroundColor: "#F05050",
				duration: 0.2,
				onComplete: () => gsap.to(this.answerInput, { backgroundColor: "white", duration: 0.2 })
			});
			this.answerInput.value = "";
		}
	}

	/**
	 * Snapshot incrémental de `.best`/`.bestRun`, à chaque bonne réponse, pas
	 * seulement à la fin : contrairement aux jeux à vies, une partie ici peut
	 * ne jamais se terminer (pas de défaite qui la clôt), donc sans ça un
	 * joueur qui abandonne en cours de route n'aurait jamais de score enregistré.
	 * Ne touche pas aux statistiques cumulées : ça reste le rôle de
	 * `completeGame()`, appelé une seule fois, à la vraie fin. La synchro, elle,
	 * est planifiée ici comme pour la progression lettre par lettre de Country
	 * Nerd : sans ça, un abandon en cours de route (30/50 États puis onglet
	 * fermé) ne laissait aucune trace côté serveur, ni pour le classement ni
	 * pour une reprise depuis un autre appareil.
	 */
	saveBestSnapshot(score) {
		const duration = this.elapsedSeconds();
		const previousBest = parseInt(StorageService.load(`${this.gameId}.best`, 0), 10) || 0;
		if (score > previousBest) StorageService.save(`${this.gameId}.best`, score);

		const candidate = sanitizeRun({ score, errors: this.errorsCount, duration, recordedAt: Date.now() });
		saveWeekRun(this.gameId, candidate);
		const existing = sanitizeRun(StorageService.load(`${this.gameId}.bestRun`));
		if (!existing || this.isBetterRun(candidate, existing)) {
			StorageService.save(`${this.gameId}.bestRun`, candidate);
		}
		AccountSync.scheduleSync();
	}

	isBetterRun(a, b) {
		if (a.score !== b.score) return a.score > b.score;
		const ea = a.errors ?? Infinity, eb = b.errors ?? Infinity;
		if (ea !== eb) return ea < eb;
		return (a.duration ?? Infinity) < (b.duration ?? Infinity);
	}

	/**
	 * Clôt une liste complétée. Gardé une seule fois par le flag `finished` et
	 * par "le pool était déjà vide au chargement", même double garde que
	 * BaseGame.recordWin(), pour la même raison : un rechargement sur l'écran
	 * de victoire ne doit pas recompter une partie.
	 */
	completeGame() {
		this.stopTicker();
		// Figé ici plutôt que recalculé au clic sur « Partager » : le partage peut
		// arriver bien après l'arrivée, le chrono ne doit plus avancer entre-temps.
		this.finalDuration = this.elapsedSeconds();
		if (!StorageService.load(`${this.gameId}.finished`) && !this.alreadyFinishedOnLoad) {
			ScoreUtils.saveStats(this.gameId, this.total);
			AccountSync.push();
		}
		StorageService.save(`${this.gameId}.finished`, true);

		SoundManager.victory();
		this.showWinState();
	}

	showWinState() {
		enterEndGame(this.page);
		this.answerContainer.classList.add("win");
		this.initShare(this.page.querySelector(".btn-share"));
		this.showLeaderboardLink();
		const hintControl = this.page.querySelector(".hint-control");
		if (hintControl) hintControl.classList.add("hidden");
	}

	initShare(btn) {
		if (!btn) return;
		btn.classList.remove("hidden");
		btn.addEventListener("click", () => {
			const lang = geoNerdApp.languageManager;
			const duration = this.finalDuration ?? this.elapsedSeconds();
			// Pas de vies dans ce mode : le score partagé porte les fautes et le
			// temps, mêmes informations que le classement (`isLetters` réutilisé
			// pour ce mode, voir gameGroups.js).
			const errorsText = `✗${this.errorsCount} ${lang.t("leaderboard.errors").toLowerCase()}`;
			geoNerdApp.shareResult(
				btn,
				lang.t("home.usStates"),
				lang.t("home.list"),
				`${this.total}/${this.total} · ${errorsText} · ⏱${formatDuration(duration)}`,
				"🟩",
				"us-states-list"
			);
		});
	}

	/**
	 * Lien vers le classement, comme les autres modes classés (injecté par
	 * `ScoreUtils.renderEndGameResults()`) — absent ici car `UsStatesList` ne
	 * dérive pas de `BaseGame` et ne passe donc jamais par cette méthode.
	 */
	showLeaderboardLink() {
		const buttons = this.page.querySelector(".country-nerd-buttons");
		if (!buttons || buttons.querySelector(".btn-leaderboard")) return;
		const lang = geoNerdApp.languageManager;
		const isEn = window.location.pathname.startsWith("/en");
		const href = `${isEn ? "/en" : ""}/leaderboard#${this.gameId}`;
		buttons.insertAdjacentHTML("beforeend", `<a href="${href}" class="btn btn-leaderboard"><span class="leaderboard-icon"></span>${lang.t("leaderboard.viewLeaderboard")}</a>`);
	}
}
